<template>
  <div class="content" id="edit-account">
    <h1>{{ pageTitle }}</h1>
    <div class="content-body two-columns form-wrapper">
      <div class="left">
        <div class="block">
          <Spin fix v-if="loading"></Spin>
          <Form v-else ref="accountFormRef" name="accountForm" :rules="accountFormRules" :model="account">
            <Row :gutter="32">
              <Col span="12">
              <FormItem :label="$t('account.edit.name')" prop="name" required>
                <Input v-model="account.name"></Input>
              </FormItem>
              </Col>
              <Col span="12">
              <FormItem :label="$t('account.edit.fullname')" prop="name" required>
                <Input v-model="account.fullname"></Input>
              </FormItem>
              </Col>
            </Row>
            <Row :gutter="32">
              <Col span="6">
              <FormItem :label="$t('account.edit.short_name')" prop="name">
                <Input v-model="account.short_name"></Input>
              </FormItem>
              </Col>
              <Col span="6">
              <FormItem :label="$t('account.edit.report_name')" prop="name">
                <Input v-model="account.report_name"></Input>
                <div style="color: #808695; text-align: right; font-size: 12px;">{{ $t('account.edit.report_name_desc')
                }}</div>
              </FormItem>
              </Col>
              <Col span="6">
              <FormItem :label="$t('account.edit.account_number')" prop="account_number">
                <Input v-model="account.account_number" disabled maxlength="10" show-word-limit></Input>
              </FormItem>
              </Col>
              <Col span="6">
              <FormItem :label="$t('account.edit.short_code')" prop="short_code">
                <Input v-model="account.short_code" @on-change="transformUpperCase" maxlength="5"
                  show-word-limit></Input>
                <div class="text-align-right" style="font-size: 12px; color: rgb(128, 134, 149)">Example, EML for Emlid.
                </div>
              </FormItem>
              </Col>
              <Col span="12">
              <FormItem :label="$t('account.edit.account_manager')" prop="account_manager_id">
                <PeopleSelect v-model="account.account_manager_id" :people.sync="account.account_manager">
                </PeopleSelect>
              </FormItem>
              </Col>
              <Col span="6">
              <FormItem :label="$t('account.edit.company')" prop="company_id"
                :rules="{ required: true, message: $t('account.edit.company_is_required') }">
                <CompanySelect v-model="account.company_id" v-bind:company.sync="account.company"></CompanySelect>
              </FormItem>
              </Col>
              <Col span="6">
              <FormItem :label="$t('account.edit.country')" prop="country_code"
                :rules="{ required: true, message: $t('account.edit.country_is_required') }">
                <CountrySelect v-model="account.country_code"></CountrySelect>
              </FormItem>
              </Col>
            </Row>
            <Row :gutter="32">
              <Col span="6">
              <FormItem :label="$t('org.preferred_currency')" prop="preferred_currency"
                :rules="{ required: true, message: $t('account.edit.preferred_currency_is_required') }">
                <CurrencySelect v-model="account.preferred_currency"></CurrencySelect>
              </FormItem>
              </Col>
              <Col span="6">
              <FormItem :label="$t('account.attribution')" prop="attribution"
                :rules="{ required: true, message: $t('account.edit.attribution_is_required') }">
                <Select v-model="account.attribution">
                  <Option value="accelerating_center">{{ $t('org.accelerating_center') }}</Option>
                  <Option value="delivery_center">{{ $t('org.delivery_center') }}</Option>
                  <Option value="dm">{{ $t('org.dm') }}</Option>
                  <Option value="trading">{{ $t('org.trading') }}</Option>
                  <Option value="other">{{ $t('org.other') }}</Option>
                </Select>
              </FormItem>
              </Col>
              <Col span="6">
              <FormItem :label="$t('account.edit.category')" prop="category"
                :rules="{ required: true, message: $t('account.edit.category_is_required') }">
                <Select v-model="account.category">
                  <Option value="A" :label="$t('org.company_category_a')">
                    <div>{{ $t('org.company_category_a') }}</div>
                    <div style="font-size: 80%; color: #808695;">{{ $t('org.company_category_a_desc') }}</div>
                  </Option>
                  <Option value="B" :label="$t('org.company_category_b')">
                    <div>{{ $t('org.company_category_b') }}</div>
                    <div style="font-size: 80%; color: #808695;">{{ $t('org.company_category_b_desc') }}</div>
                  </Option>
                  <Option value="C" :label="$t('org.company_category_c')">
                    <div>{{ $t('org.company_category_c') }}</div>
                    <div style="font-size: 80%; color: #808695;">{{ $t('org.company_category_c_desc') }}</div>
                  </Option>
                  <Option value="D" :label="$t('org.company_category_d')">
                    <div>{{ $t('org.company_category_d') }}</div>
                    <div style="font-size: 80%; color: #808695;">{{ $t('org.company_category_d_desc') }}</div>
                  </Option>
                </Select>
              </FormItem>
              </Col>
            </Row>

            <FormItem class="text-align-right">
              <Button type="primary" :loading="saving" style="margin-right:10px;" @click="saveAccountForm()">
                {{ $t('form.save') }}
              </Button>
            </FormItem>
          </Form>
        </div>
        <div class="clearfix"></div>

        <div style="background-color: #fff; padding: 25px; margin-top: 25px;">
          <h2>{{ $t('account.edit.customer_information') }}</h2>
          <div class="text-align-right">
            <Button type="primary" class="margin-bottom-10" :disabled="disabled" @click="showAddCustomerModel = true">{{
                $t('account.edit.add_customer_information')
            }}</Button>
          </div>
          <vxe-table resizable border size="mini" :loading="loading" show-overflow="tooltip" highlight-current-row
            highlight-hover-row ref="xTree" :scroll-y="{ enabled: false, gt: -1 }" :data="customerInformationData">
            <vxe-table-column title="#" type="seq" width="45" align="center"></vxe-table-column>
            <vxe-table-column field="part_number" :title="$t('account.customer_information_form.part_number')"
              min-width="120"></vxe-table-column>
            <vxe-table-column field="manufacturer_part.name" :title="$t('account.customer_information_form.name')"
              min-width="180"></vxe-table-column>
            <vxe-table-column field="manufacturer_part.specification"
              :title="$t('account.customer_information_form.specification')" min-width="120"></vxe-table-column>
            <vxe-table-column field="manufacturer_part.brand" :title="$t('account.customer_information_form.brand')"
              min-width="80"></vxe-table-column>
            <vxe-table-column field="manufacturer_part.mpn" :title="$t('account.customer_information_form.mpn')"
              min-width="100"></vxe-table-column>
            <vxe-table-column field="scrap" :title="$t('account.customer_information_form.stock')" min-width="60">
              <template v-slot="{ row }">
                <ErpStocks v-if="row" :partNumber="_.get(row, 'part_number')">
                </ErpStocks>
              </template>
            </vxe-table-column>
            <vxe-table-column field="manufacturer_part.moderation_state"
              :title="$t('account.customer_information_form.material_state')" min-width="60"></vxe-table-column>
            <vxe-table-column field="enabled"
              :title="$t('account.customer_information_form.customer_information_state')" min-width="60">
              <template v-slot="{ row }">
                <!-- {{ row.enabled ? '启用' : '禁用' }} -->
                <i-switch v-model="row.enabled" @on-change="handleChangeState(row)" size="large">
                  <span slot="open">{{ $t('account.customer_information_form.status_on') }}</span>
                  <span slot="close">{{ $t('account.customer_information_form.status_off') }}</span>
                </i-switch>
              </template>
            </vxe-table-column>
            <vxe-table-column field="action" :title="$t('account.customer_information_form.action')" min-width="60"
              align="center">
              <template v-slot="{ row }">
                <Poptip confirm :width="250" style="text-align:left;" :title="$t('cost.materials.delete_confirm')"
                  transfer @on-ok="handleDeleteCustomerProvideInfo(row)">
                  <Button type="text" size="small" color="red" icon="md-trash"></Button>
                </Poptip>
              </template>
            </vxe-table-column>
          </vxe-table>
        </div>

        <div>
          <AccountRecentActivities :accountId="accountId"></AccountRecentActivities>
        </div>

      </div>
      <div class="right">
        <div class="block">
          <h3>{{ $t('org.company_related_object') }}</h3>
          <div v-for="o in account.external_object_relation" :key="o.id">
            <Card>
              <p slot="title">{{ o.external_name.toUpperCase() }}</p>
              <div>ID: {{ o.external_object_id }}</div>
              <div>{{ $t('type') }}: {{ o.external_object_type }}</div>
              <div>{{ $t('date') }}: {{ o.created_at }}</div>
            </Card>
          </div>
        </div>
      </div>
    </div>
    <Modal v-model="showAddCustomerModel" footer-hide :closable="false" transfer width="50%"
      :title="$t('account.edit.add_customer_information')">
      <Form ref="displayNameInfo" :model="selectedRow" :label-width="100">
        <ManufacturerPartSelect ref="manufacturerPartSelect" @update:manufacturerPart="handleLoadPartInfo">
        </ManufacturerPartSelect>
        <FormItem label="PN" prop="part_number">
          <Input type="text" v-model="selectedRow.part_number"></Input>
        </FormItem>
        <FormItem label="Brand" prop="brand">
          <Input type="text" disabled v-model="selectedRow.brand"></Input>
        </FormItem>
        <FormItem label="MPN" prop="mpn">
          <Input type="text" disabled v-model="selectedRow.mpn"></Input>
        </FormItem>
        <FormItem label="Name" prop="name">
          <Input type="text" disabled v-model="selectedRow.name"></Input>
        </FormItem>
        <FormItem label="Specification" prop="specification">
          <Input type="text" disabled v-model="selectedRow.specification"></Input>
        </FormItem>
        <FormItem class="text-align-right">
          <Button @click="showAddCustomerModel = false" class="margin-right-10">{{ $t('cost.materials.form.cancel') }}
          </Button>
          <Button type="primary" :loading="saving" @click="handleSaveCustomerProvideInfo">{{
              $t('cost.materials.form.save')
          }}</Button>
        </FormItem>
      </Form>
    </Modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ManufacturerPartSelect from '@/view/part/components/manufacturer-part-select'
import { PeopleSelect } from '@/view/people/components'
import { CompanySelect } from '@/view/companies/components'
import ErpStocks from '@/view/part/components/stocks'
import { CountrySelect } from '@/components/nexpcb'
import CurrencySelect from '@/components/currency'
import AccountRecentActivities from '@/view/account/components/widgets/account-recent-activities.vue'

export default {
  name: 'EditAccount',
  data() {
    return {
      account: {
        short_code: ''
      },
      accountId: this.$route.params.accountId,
      saving: false,
      loading: false,
      ruleValidate: {},
      showAddCustomerModel: false,
      customerInformationData: [],
      selectedRow: {},
      accountFormRules: {
        short_code: [
          { validator: (rule, value, cb) => { return /\w/.test(value) ? cb() : cb(new Error('Short code only contains alpha, underscore and number, for example, EML, EN_001.')) }, trigger: 'blur' }
        ]
      }
    }
  },
  components: {
    ManufacturerPartSelect,
    ErpStocks,
    PeopleSelect,
    CompanySelect,
    CountrySelect,
    CurrencySelect,
    AccountRecentActivities
  },
  computed: {
    pageTitle() {
      let title = this.$t('menu.account.edit_account') + ' - ' + this.account?.name
      document.title = title + ' - ' + this.account?.id
      return title
    },
    partNumbers: function () {
      if (this.customerInformationData) {
        return _.uniq(_.map(this.customerInformationData, (item) => {
          return _.get(item, 'part_number')
        }))
      } else {
        return []
      }
    },
    disabled() {
      return !this.account.short_code
    }
  },
  mounted() {
    this.loadAccount(this.accountId)
    this.loadCustomerProvidedData(this.accountId)
  },
  watch: {
    '$route.params.accountId': function (newVal) {
      this.loadAccount(newVal)
      this.loadCustomerProvidedData(newVal)
    },
    customerInformationData() {
      if (this.customerInformationData.length > 0) {
        this.getErpStocks({ part_numbers: this.partNumbers })
      }
    },
    'account.company.country_code'(value) {
      if (value) {
        this.account.country_code = value
      }
    }
  },
  methods: {
    ...mapActions([
      'getAccount',
      'updateAccount',
      'getCustomerProvidedByAccount',
      'updateCustomerProvidedByAccount',
      'deleteCustomerProvidedByAccount',
      'getErpStocks'
    ]),
    loadAccount(id) {
      this.loading = true
      this.getAccount(id)
        .then(res => {
          this.account = res
        }).finally(() => {
          this.loading = false
        })
    },
    saveAccountForm() {
      this.$refs['accountFormRef'].validate((valid) => {
        if (valid) {
          this.saving = true
          if (this.account.short_code) {
            this.account.short_code = this.account.short_code.toUpperCase()
          }
          this.updateAccount({ id: this.accountId, data: this.account }).then(res => {
            this.$Message.success('Save successful!')
            this.account = res
          }).finally(() => {
            this.saving = false
          })
        }
      })
    },
    loadCustomerProvidedData(id) {
      this.loading = true
      this.getCustomerProvidedByAccount(id).then(res => {
        this.customerInformationData = res.data
        _.forEach(this.customerInformationData, (item) => {
          item.enabled = !!item.enabled
        })
      }).finally(() => {
        this.loading = false
      })
    },
    handleLoadPartInfo(manufacturer_part) {
      if (manufacturer_part) {
        this.$set(this.selectedRow, 'brand', manufacturer_part.brand)
        this.$set(this.selectedRow, 'mpn', manufacturer_part.mpn)
        this.$set(this.selectedRow, 'name', manufacturer_part.name)
        this.$set(this.selectedRow, 'specification', manufacturer_part.specification)
        this.$set(this.selectedRow, 'part_number', manufacturer_part.part_identity ? manufacturer_part.part_identity.part_number + '-' + this.account.short_code : '')
        this.$set(this.selectedRow, 'manufacturer_part_id', manufacturer_part.id)
      }
    },
    handleSaveCustomerProvideInfo() {
      this.$set(this.selectedRow, 'account_id', Number(this.accountId))
      this.saving = true
      this.updateCustomerProvidedByAccount(this.selectedRow).then(res => {
        this.loadCustomerProvidedData(this.accountId)
        this.$Message.success('Add successful!')
        this.showAddCustomerModel = false
      }).finally(() => {
        this.saving = false
      })
    },
    handleChangeState(row) {
      let selectedRow = _.cloneDeep(row)
      this.$set(selectedRow, 'account_id', Number(this.accountId))
      this.updateCustomerProvidedByAccount(selectedRow).then(res => {
        this.$Message.success('Edit successful!')
      })
    },
    handleDeleteCustomerProvideInfo(row) {
      this.deleteCustomerProvidedByAccount(row.id).then(res => {
        this.$Message.success('Successfully deleted!')
        this.loadCustomerProvidedData(this.accountId)
      })
    },
    transformUpperCase(event) {
      this.account.short_code = event.target.value.toUpperCase()
    }
  }
}
</script>

<style lang="less">
#edit-account {
  .w60 {
    width: 60%;
  }
}
</style>
